import performQuery, {Middleware} from './performQuery';
import {
  BELineupData,
  BEMatchInfo,
  BEMatchLiveData,
  BEMatchSimple,
  BEMatchStats,
  BESport,
} from './types';
import {createMiddleware} from './utils';
import validateBEMatchSimpleArray from './validation';

/**
 * Performs a GraphQL query that retrieves the sport events of a defined day
 * @param date Date in format "yyyy-MM-dd"
 */
export const sportEventsByDate = (date: string): Promise<BEMatchSimple[]> => {
  const getRoot: Middleware = createMiddleware('sportEventsByDate');
  const query = `{
    sportEventsByDate(date: "${date}") {
      _id
      sportRadarEventId
      sportEventSlug
      sportType
      startDate
      seasonCompetitionId
      competitionGroup
      competitionGroupId
      competitionSubGroup
      status
      liveBroadcast {
        channel
        activity
      }
      minute
      stoppageTime
      matchStatus
      homeScore
      awayScore
      season {
        name
        competition {
          name
          sportType
          competitionSlug
        }
      }
      competitors {
        logoUrl
        teamName
        teamSlug
        teamAbbreviation
        countryCode
      }
      periodScores {
        homeScore
        awayScore
        periodType
        periodNumber
      }
    }
  }`;

  return performQuery(query, getRoot)();
};

/**
 * Performs a GraphQL query that retrieves the sport events by competitionId
 * by month
 * @param competitionId
 * @param month in format "yyyy-MM"
 */
export const sportEventsByCompetitionIdAndMonth = (
  competitionId: string,
  month: string
): Promise<BEMatchSimple[]> => {
  const getRoot: Middleware = createMiddleware('sportEventsByCompetitionOrSportTypeAndMonth');
  const query = `{
    sportEventsByCompetitionOrSportTypeAndMonth(competitionId: "${competitionId}", month: "${month}") {
      _id
      sportRadarEventId
      sportEventSlug
      sportType
      startDate
      seasonCompetitionId
      competitionGroup
      competitionGroupId
      competitionSubGroup
      status
      liveBroadcast {
        channel
        activity
      }
      matchStatus
      minute
      stoppageTime
      homeScore
      awayScore
      season {
        name
        competition {
          name
          sportType
          competitionSlug
        }
      }
      competitors {
        logoUrl
        teamName
        teamSlug
        teamAbbreviation
        countryCode
      }
      periodScores {
        homeScore
        awayScore
        periodType
        periodNumber
      }
    }
  }`;

  return performQuery(query, getRoot)();
};

/**
 * Performs a GraphQL query that retrieves the sport events by sport
 * by month
 * @param sportType
 * @param month in format "yyyy-MM"
 */
export const sportEventsBySportTypeAndMonth = (
  sportType: BESport,
  month: string
): Promise<BEMatchSimple[]> => {
  const getRoot: Middleware = createMiddleware('sportEventsByCompetitionOrSportTypeAndMonth');
  const query = `{
    sportEventsByCompetitionOrSportTypeAndMonth(sportType: "${sportType}", month: "${month}") {
      _id
      sportRadarEventId
      sportEventSlug
      sportType
      startDate
      seasonCompetitionId
      competitionGroup
      competitionGroupId
      competitionSubGroup
      status
      liveBroadcast {
        channel
        activity
      }
      matchStatus
      minute
      stoppageTime
      homeScore
      awayScore
      season {
        name
        competition {
          name
          sportType
          competitionSlug
        }
      }
      competitors {
        logoUrl
        teamName
        teamSlug
        teamAbbreviation
        countryCode
      }
      periodScores {
        homeScore
        awayScore
        periodType
        periodNumber
      }
    }
  }`;

  return performQuery(query, getRoot)();
};

/**
 * Performs a GraphQL query that retrieves the sport events of a season
 * @param seasonId
 */
export const sportEventsBySeasonId = (seasonId: string): Promise<BEMatchSimple[]> => {
  const getRoot: Middleware = createMiddleware('sportEventsBySeasonId');
  const query = `{
    sportEventsBySeasonId(seasonId: "${seasonId}") {
      _id
      sportRadarEventId
      sportEventSlug
      sportType
      startDate
      seasonCompetitionId
      competitionGroup
      competitionGroupId
      competitionSubGroup
      status
      liveBroadcast {
        channel
        activity
      }
      matchStatus
      minute
      stoppageTime
      homeScore
      awayScore
      season {
        name
        competition {
          name
          sportType
          competitionSlug
        }
      }
      competitors {
        logoUrl
        teamName
        teamSlug
        teamAbbreviation
        countryCode
      }
      periodScores {
        homeScore
        awayScore
        periodType
        periodNumber
      }
    }
  }`;

  return performQuery(query, getRoot)();
};

/**
 * Performs a GraphQL query that retrieves the sport events from a specific Match Group
 * @param id Match Group ID (MongoID)
 */
// export const sportEventsByGroupId = (id: string): Promise<BEMatchSimple[]> => {
//   const getRoot: Middleware = createMiddleware('sportEventsByGroupId');
//   const query = `{
//     sportEventsByGroupId(id: "${id}") {
//       _id
//       sportRadarEventId
//       sportEventSlug
//       sportType
//       startDate
//       seasonCompetitionId
//       competitionGroup
//       competitionGroupId
//       status
//       minute
//       matchStatus
//       homeScore
//       awayScore
//       season {
//         name
//         competition {
//           name
//           sportType
//           competitionSlug
//         }
//       }
//       competitors {
//         logoUrl
//         teamName
//         teamSlug
//         teamAbbreviation
//         countryCode
//       }
//       periodScores {
//         homeScore
//         awayScore
//         periodType
//         periodNumber
//       }
//     }
//   }`;

//   return performQuery(query, getRoot)();
// };

export const sportEventsByGroupId = (groupId: string): Promise<BEMatchSimple[]> => {
  const getRoot: Middleware = createMiddleware('sportEventsByGroupId');
  const query = `{
    sportEventsByGroupId(groupId: "${groupId}") {
      _id
      sportRadarEventId
      sportEventSlug
      sportType
      startDate
      seasonCompetitionId
      competitionGroup
      competitionGroupId
      competitionSubGroup
      status
      minute
      liveBroadcast {
        channel
        activity
      }
      matchStatus
      homeScore
      awayScore
      season {
        name
        competition {
          name
          sportType
        }
      }
      competitors {
        logoUrl
        teamName
        teamAbbreviation
        countryCode
      }
      periodScores {
        homeScore
        awayScore
        periodType
        periodNumber
      }
    }
  }`;

  return performQuery(query, getRoot)();
};

/**
 * Performs a GraphQL query that retrieves the sport event with a specific id
 */
export const getLiveMatchScoreById = (id: string): Promise<BEMatchLiveData> => {
  const getRoot: Middleware = createMiddleware('sportEventById');
  const query = ` {
    sportEventById(id: "${id}") {
      _id
      sportType
      status
      minute
      stoppageTime
      matchStatus
      liveBroadcast {
        channel
        activity
      }
      homeScore
      awayScore
      periodScores {
        homeScore
        awayScore
        periodType
        periodNumber
      }
    }
  }`;

  return performQuery(query, getRoot)();
};

/**
 * Performs a GraphQL query that retrieves the sport event with a specific id,
 * with the projection required to compose a MatchInfo object
 */
export const sportEventInfoById = (id: string): Promise<BEMatchInfo> => {
  const getRoot: Middleware = createMiddleware('sportEventById');
  const query = ` {
    sportEventById(id: "${id}") {
      _id
      sportRadarEventId
      sportType
      startDate
      commentary {
        reporters {
          fullname
        }
        commentators {
          fullname
        }
      }
      season {
        seasonId
        competition {
          name
          sportType
          competitionSlug
        }
      }
      probabilities {
        name
        outcome
      }
      teamRankings {
        associationName
        rank
        points
        competitorId
      }
      competitionGroup
      competitionSubGroup
      competitionSubGroup
      stadium
      customStadium
      competitors {
        logoUrl
        teamName
        teamSlug
        teamAbbreviation
        teamCountry
        countryCode
        sportRadarCompetitorId
      }
      versus {
        homeAbbreviation
        homeScore
        awayAbbreviation
        awayScore
        eventDate
        matchStatus
        status
      }
      latestForms {
        qualifier
        sportEventResultList {
          teamName
          teamAbbreviation
          result
          score
        }
      }
      betMatch {
        _id
      }
      providerView
      lmtSponsor {
        panelLogo
        pitchLogo
        goalBannerLogo
      }
      tweetHashTag
      eceArticleContent {
        eceId
        title
        summary
        content
        authorName
        publishedDate
        articleSection {
          term
          label
        }
        articleImage {
          url
        }
      }
    }
  }`;
  return performQuery(query, getRoot)();
};

/**
 * Performs a GraphQL query that retrieves the sport event with a specific id,
 * with the projection required to compose a MatchLiveData object
 */
export const sportEventLiveDataById = (id: string): Promise<BEMatchLiveData> => {
  const getRoot: Middleware = createMiddleware('sportEventById'); // FIXME: move commentary to 1m polling
  const query = ` {
    sportEventById(id: "${id}") {
      _id
      status
      minute
      stoppageTime
      matchStatus
      sportType
      homeScore
      awayScore
      liveBroadcast {
        channel
        activity
      }
      commentary {
        reporters {
          fullname
        }
        commentators {
          fullname
        }
      }
      periodScores {
        homeScore
        awayScore
        periodType
        periodNumber
      }
    }
  }`;

  return performQuery(query, getRoot)();
};

/**
 * Performs a GraphQL query that retrieves the stats of a sport event with a specific id,
 */
export const statsByEventId = (id: string): Promise<BEMatchStats> => {
  const getRoot: Middleware = createMiddleware('sportEventById');
  const query = ` {
    sportEventById(id: "${id}") {
      statistics {
        qualifier
        ballPossession
        shotsTotal
        shotsOnTarget
        cornerKicks
        fouls
        offsides
        threePointAttemptsTotal
        threePointAttemptsSuccessful
        twoPointAttemptsTotal
        twoPointAttemptsSuccessful
        freeThrowAttemptsTotal
        freeThrowAttemptsSuccessful
        rebounds
        teamTurnovers
        steals
        assists
        shotsBlocked
        biggestLead
        breakpointsWon
        aces
        doubleFaults
        sevenmGoals
        saves
        shootingAccuracy
        suspensions
      }
    }
  }`;

  return performQuery(query, getRoot)();
};

/**
 * Performs a GraphQL query that retrieves the team lineups of an event with a specific id
 */
export const lineupDataByEventId = (id: string): Promise<BELineupData> => {
  const getRoot: Middleware = createMiddleware('sportEventById');
  const query = ` {
    sportEventById(id: "${id}") {
      competitors{
        qualifier
        teamName
      }
      rosters {
        qualifier
        sportEventPlayerInfos {
          isStarter
          player {
            sportRadarPlayerId
            sportType
            shirt
            name
            area
          }
        }
      }
    }
  }`;

  return performQuery(query, getRoot)();
};

export default {
  sportEventsByDate,
  sportEventsBySeasonId,
  sportEventInfoById,
  sportEventLiveDataById,
  getLiveMatchScoreById,
  lineupDataByEventId,
  sportEventsByCompetitionAndMonth: sportEventsByCompetitionIdAndMonth,
  sportEventsBySportAndMonth: sportEventsBySportTypeAndMonth,
};




// sport event by sport and currentdate
export const sportEventsBySportTypeAndDate = (
  sportType: BESport,
  date: string
): Promise<BEMatchSimple[]> => {
  const getRoot: Middleware = createMiddleware('sportEventsBySportTypeAndDate');
  const query = `{
    sportEventsBySportTypeAndDate(sportType: "${sportType}", month: "${date}") {
      _id
      sportRadarEventId
      sportEventSlug
      sportType
      startDate
      seasonCompetitionId
      competitionGroup
      competitionGroupId
      competitionSubGroup
      status
      matchStatus
      liveBroadcast {
        channel
        activity
      }
      minute
      homeScore
      awayScore
      season {
        name
        competition {
          name
          sportType
          competitionSlug
        }
      }
      competitors {
        logoUrl
        teamName
        teamSlug
        teamAbbreviation
        countryCode
      }
      periodScores {
        homeScore
        awayScore
        periodType
        periodNumber
      }
    }
  }`;

  return performQuery(query, getRoot)();
};